/**
 * Register watch — the server's watch over new HRB entries, as the owner sees it.
 *
 * Shows whether the watch runs (and why not), how much of the portal's hourly
 * budget it used, and the new companies with their scored business purpose.
 * Resume and Pause are the owner's switch: after the portal refused the watch,
 * only a person decides whether it may try again. Kate's decisions on entries
 * arrive through her `registerwatch_*` tools, not here.
 *
 * Owns its queries (a sanctioned exception, like `PipelineMemberships`): the
 * keys sit under `["lead-sources", "register", …]`, so the page's family
 * invalidation repaints it too.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  registerWatchApi,
  type RegisterEntry,
  type RegisterReviewStatus,
  type RegisterWatchStatus,
} from "../../api/registerWatch";
import { formatRelative } from "../../lib/formatRelative";
import { useConfirm } from "../confirm/context";
import { Drawer } from "../admin/Drawer";
import { Pill, type PillTone } from "../Pill";
import { QueryBoundary } from "../QueryBoundary";
import { StatTile } from "../StatTile";
import { useToast } from "../toast/context";
import { FilterChips } from "../ui/FilterChips";

const POLL_MS = 30_000;
const KEY = ["lead-sources", "register"] as const;

const FILTERS: { value: "all" | RegisterReviewStatus; label: string }[] = [
  { value: "pending_review", label: "Waiting for Kate" },
  { value: "proposed", label: "Proposed" },
  { value: "skipped", label: "Skipped" },
  { value: "low_score", label: "Low score" },
  { value: "not_new", label: "Not new" },
  { value: "all", label: "All" },
];

const PAUSE_TEXT: Record<string, string> = {
  initial: "Not started yet",
  manual: "Paused by hand",
  http_status: "The portal refused a request",
  blocked: "The portal showed its block page",
  captcha: "The portal asked for a captcha",
  unexpected: "The portal answered with unexpected pages",
  errors: "The portal kept failing",
  session: "The portal kept dropping the session",
};

const HARD_STOPS = new Set(["http_status", "blocked", "captcha"]);

function watchState(s: RegisterWatchStatus): { label: string; tone: PillTone; detail: string } {
  if (s.env_disabled) {
    return { label: "switched off", tone: "muted", detail: "REGISTER_WATCH_DISABLED is set on the server." };
  }
  if (s.paused) {
    const why = PAUSE_TEXT[s.paused_kind ?? ""] ?? "Paused";
    return {
      label: "paused",
      tone: HARD_STOPS.has(s.paused_kind ?? "") ? "alert" : "signal",
      detail: `${why}${s.paused_reason ? ` – ${s.paused_reason}` : ""}${
        s.paused_at ? ` · ${formatRelative(s.paused_at)}` : ""
      }`,
    };
  }
  if (s.backoff_until && new Date(s.backoff_until) > new Date()) {
    return { label: "backing off", tone: "signal", detail: s.last_error ?? "Server errors – waiting before the next try." };
  }
  return {
    label: "running",
    tone: "ok",
    detail: `At most ${s.hourly_cap} requests an hour${s.quiet_hours ? `, none ${s.quiet_hours}` : ""}.`,
  };
}

/** "69 walks due · oldest 3d ago · 47 still starting" – whether the walks keep up. */
function courtsDelta(s: RegisterWatchStatus): string | undefined {
  const parts: string[] = [];
  if (s.walks_due > 0) parts.push(`${s.walks_due} walks due · oldest ${formatRelative(s.walk_due_since)}`);
  const starting = (s.sequences.unseeded ?? 0) + (s.sequences.bootstrap ?? 0);
  if (starting > 0) parts.push(`${starting} still starting`);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/** Live purposes and the look back's apart: the look back is background work. */
function pendingDelta(s: RegisterWatchStatus): string | undefined {
  const parts: string[] = [];
  if (s.purpose_pending > 0) parts.push(`${s.purpose_pending} purposes pending`);
  if (s.lookback_pending > 0) parts.push(`look back: ${s.lookback_pending} to fetch`);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function reviewTone(status: RegisterReviewStatus): PillTone {
  if (status === "pending_review") return "signal";
  if (status === "proposed") return "ok";
  return "muted";
}

export function RegisterWatchPanel({
  entryId,
  onOpenEntry,
}: {
  entryId: string | null;
  onOpenEntry: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [filter, setFilter] = useState<"all" | RegisterReviewStatus>("pending_review");

  const status = useQuery({ queryKey: [...KEY, "status"], queryFn: registerWatchApi.status, refetchInterval: POLL_MS });
  const entries = useQuery({
    queryKey: [...KEY, "entries", filter],
    queryFn: () => registerWatchApi.entries({ status: filter, limit: 200 }),
    refetchInterval: POLL_MS,
  });
  const entry = useQuery({
    queryKey: [...KEY, "entry", entryId],
    queryFn: () => registerWatchApi.entry(entryId as string),
    enabled: !!entryId,
  });

  const switchWatch = useMutation({
    mutationFn: (action: { kind: "resume" } | { kind: "pause"; reason: string }) =>
      action.kind === "resume" ? registerWatchApi.resume() : registerWatchApi.pause(action.reason),
    onSuccess: (_d, action) => {
      toast.ok(action.kind === "resume" ? "Register watch resumed." : "Register watch paused.");
      return qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (err) => toast.error(err),
  });

  async function onResume(s: RegisterWatchStatus) {
    const hard = HARD_STOPS.has(s.paused_kind ?? "");
    const ok = await confirm({
      title: s.paused_kind === "initial" ? "Start the register watch?" : "Resume the register watch?",
      body: hard
        ? "The portal refused the watch. Resuming sends requests again from the production IP – a second refusal can mean a lasting block."
        : "The server sends at most one request a minute to handelsregister.de, and stops on its own at the first refusal.",
      confirmLabel: s.paused_kind === "initial" ? "Start" : "Resume",
      tone: hard ? "danger" : "default",
    });
    if (ok) switchWatch.mutate({ kind: "resume" });
  }

  async function onPause() {
    const ok = await confirm({ title: "Pause the register watch?", body: "Nothing is sent until you resume it.", confirmLabel: "Pause" });
    if (ok) switchWatch.mutate({ kind: "pause", reason: "paused in the WebUI" });
  }

  const s = status.data;
  const state = s ? watchState(s) : null;
  return (
    <div className="space-y-5">
      <div className="panel flex flex-wrap items-center gap-3">
        {state ? <Pill value={state.label} tone={state.tone} /> : <span className="text-sm text-ink-low">Loading…</span>}
        <span className="text-sm text-ink-medium flex-1 min-w-[240px]">{state?.detail}</span>
        {s && !s.env_disabled ? (
          s.paused ? (
            <button type="button" className="btn btn-main btn-sm" disabled={switchWatch.isPending} onClick={() => onResume(s)}>
              {s.paused_kind === "initial" ? "Start" : "Resume"}
            </button>
          ) : (
            <button type="button" className="btn btn-ghost btn-sm" disabled={switchWatch.isPending} onClick={onPause}>
              Pause
            </button>
          )
        ) : null}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatTile
          label="Requests · last hour"
          figure={s ? <span className="tnum">{`${s.requests_last_hour} / ${s.hourly_cap}`}</span> : "…"}
          delta={s ? `${s.requests_today} today` : undefined}
        />
        <StatTile label="New today" figure={s ? String(s.new_today) : "…"} />
        <StatTile
          label="Waiting for Kate"
          figure={s ? String(s.entries.pending_review ?? 0) : "…"}
          deltaTone={s && (s.entries.pending_review ?? 0) > 0 ? "warn" : null}
        />
        <StatTile label="Proposed" figure={s ? String(s.entries.proposed ?? 0) : "…"} />
        <StatTile
          label="Courts watched"
          figure={s ? String((s.sequences.active ?? 0) + (s.sequences.dormant ?? 0)) : "…"}
          delta={s ? courtsDelta(s) : undefined}
        />
        <StatTile
          label="Last hit"
          figure={s?.last_hit_at ? <span className="text-base">{formatRelative(s.last_hit_at)}</span> : "–"}
          delta={s ? pendingDelta(s) : undefined}
        />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <FilterChips
          aria-label="Entry status"
          options={FILTERS.map((f) => ({
            value: f.value,
            label: f.label,
            count: s && f.value !== "all" ? (s.entries[f.value] ?? 0) : null,
          }))}
          value={filter}
          onChange={(value) => setFilter(value as typeof filter)}
        />
      </div>

      <QueryBoundary
        query={entries}
        isEmpty={(d) => d.entries.length === 0}
        empty={
          <div className="empty-state">
            <div className="title">No entries here</div>
            <p className="hint">New companies appear once the watch finds them and their business purpose is scored.</p>
          </div>
        }
      >
        {(data) => (
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Register</th>
                <th>Seat</th>
                <th className="text-right">Score</th>
                <th>Status</th>
                <th>Found</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <EntryRow key={e.id} entry={e} onOpen={() => onOpenEntry(e.id)} />
              ))}
            </tbody>
          </table>
        )}
      </QueryBoundary>

      {s?.recent_requests?.length ? (
        <details className="panel">
          <summary className="cursor-pointer text-sm text-ink-medium">Last {s.recent_requests.length} requests</summary>
          <table className="table mt-3">
            <tbody>
              {s.recent_requests.map((r, i) => (
                <tr key={`${r.at}-${i}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{r.at ? formatRelative(r.at) : "–"}</td>
                  <td className="font-mono text-xs">{r.kind}</td>
                  <td className="font-mono text-xs">
                    {r.court_code ?? ""} {r.register_number ?? ""}
                  </td>
                  <td className="font-mono text-xs">{r.outcome}</td>
                  <td className="font-mono text-xs text-ink-low">
                    {r.http_status ?? ""} {r.duration_ms != null ? `${r.duration_ms} ms` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}

      <EntryDrawer entry={entry.data} loading={entry.isLoading} open={!!entryId} onClose={() => onOpenEntry(null)} />
    </div>
  );
}

function EntryRow({ entry, onOpen }: { entry: RegisterEntry; onOpen: () => void }) {
  return (
    <tr className="cursor-pointer" onClick={onOpen}>
      <td>
        <div className="font-heading font-semibold text-ink">{entry.name}</div>
        {entry.former_court ? <div className="text-xs text-ink-low">moved from {entry.former_court}</div> : null}
        {entry.origin === "lookback" ? <div className="text-xs text-ink-low">look back · registered earlier</div> : null}
      </td>
      <td className="font-mono text-xs whitespace-nowrap">
        {entry.court_name} · HRB {entry.register_number}
      </td>
      <td className="text-sm text-ink-medium">{entry.seat ?? "–"}</td>
      <td className="text-right font-mono tnum">{entry.score ?? "–"}</td>
      <td>
        <Pill value={entry.review_status.replace("_", " ")} tone={reviewTone(entry.review_status)} />
      </td>
      <td className="text-sm text-ink-low tnum whitespace-nowrap">
        {entry.first_seen_at ? formatRelative(entry.first_seen_at) : "–"}
      </td>
    </tr>
  );
}

function EntryDrawer({
  entry,
  loading,
  open,
  onClose,
}: {
  entry: RegisterEntry | undefined;
  loading: boolean;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Drawer open={open} onClose={onClose} eyebrow="Register entry" title={entry?.name ?? "…"}>
      {loading && !entry ? (
        <div className="text-sm text-ink-low">Loading…</div>
      ) : entry ? (
        <div className="space-y-5 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Pill value={entry.review_status.replace("_", " ")} tone={reviewTone(entry.review_status)} />
            <span className="font-mono text-xs">
              Amtsgericht {entry.court_name} · HRB {entry.register_number}
            </span>
          </div>
          <dl className="grid grid-cols-[140px_1fr] gap-y-1.5">
            <dt className="text-ink-low">Seat</dt>
            <dd>
              {entry.seat ?? "–"} {entry.postal_code ? <span className="text-ink-low">({entry.postal_code})</span> : null}
            </dd>
            <dt className="text-ink-low">Registered</dt>
            <dd className="font-mono">{entry.founded_on ?? "–"}</dd>
            <dt className="text-ink-low">Capital</dt>
            <dd className="font-mono">
              {entry.capital_amount ? `${entry.capital_amount} ${entry.capital_currency ?? ""}` : "–"}
            </dd>
            <dt className="text-ink-low">Managing directors</dt>
            <dd className="font-mono">{entry.directors_count ?? "–"}</dd>
            <dt className="text-ink-low">Score</dt>
            <dd className="font-mono">{entry.score ?? "–"}</dd>
            <dt className="text-ink-low">Found</dt>
            <dd>
              {entry.first_seen_at ? formatRelative(entry.first_seen_at) : "–"}
              {entry.origin === "lookback" ? <span className="text-ink-low"> · look back (GründungsIndex list)</span> : null}
            </dd>
          </dl>
          {entry.matched_terms.length ? (
            <div className="flex flex-wrap gap-1">
              {entry.matched_terms.map((t) => (
                <span key={t} className="pill pill--muted">
                  {t}
                </span>
              ))}
            </div>
          ) : null}
          <section>
            <span className="label">Business purpose (Unternehmensgegenstand)</span>
            <p className="text-ink-medium whitespace-pre-wrap">{entry.purpose ?? "Not fetched yet."}</p>
          </section>
          {entry.review_note ? (
            <section>
              <span className="label">Kate's note</span>
              <p className="text-ink-medium">{entry.review_note}</p>
            </section>
          ) : null}
        </div>
      ) : (
        <div className="text-sm text-ink-low">Entry not found – it may have been pruned after 30 days.</div>
      )}
    </Drawer>
  );
}
